import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Button from 'components/Button'
import * as yup from 'yup'
import { Form } from 'react-final-form'
import { useTranslation } from 'react-i18next'
import { Grid, makeStyles, Typography } from '@material-ui/core'
import { useSelector, useDispatch } from 'react-redux'
import { getTemas } from 'store/modules/temas/temas.selectors'
import { getGrupos } from 'store/modules/grupos/grupos.selectors'
import { getGrupoTemas, getIsSavingGrupoTema } from 'store/modules/grupoTema/grupoTema.selectors'
import { useConfirmationDialog } from 'components/Dialog/ConfirmDialog'
import { FieldText, FieldSelect, FieldAutocomplete } from 'components/Fields'
import { callSalvarListaGrupoTema, callExcluirGrupoTema } from 'store/modules/grupoTema'
import { getQualificadores } from 'store/modules/qualificadores/qualificadores.selectors'
import { validateFormValues } from 'utils/forms'
import clsx from 'clsx'
import Accordion from 'components/Accordion'
import { filter } from 'lodash'
import { getTipoUsuarioLogado } from 'store/modules/auth/auth.selectors'
import { TIPO_USUARIO } from 'utils/constantes'

const useStyles = makeStyles((theme) => ({
  sectionTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.grey.main}`,
  },
  screenTitle: {
    display: 'flex',
    alignItems: 'center',
    height: theme.spacing(5),
    fontSize: theme.typography.pxToRem(24),
  },
  cancelButton: {
    marginRight: 'auto',
  },
  group: {
    marginTop: '32px',
    border: '1px solid #C9CEDB',
    boxShadow: '0px 8px 32px rgba(15, 52, 68, 0.08)',
    borderRadius: '8px',
    padding: '0px 16px',
  },
  divider: {
    height: 1,
    backgroundColor: theme.palette.grey.main,
    width: 'calc(100% + 32px)',
    marginLeft: '-16px',
  },
  itemTitle: {
    fontFamily: 'Poppins',
    color: '#0F3444',
    fontWeight: '600',
    fontSize: '16px',
  },
  formAdd: {
    transition: '0.20s ease-out',
    maxHeight: 0,
    overflow: 'hidden',
  },
  openFormAdd: {
    paddingTop: 20,
    marginTop: '22px',
    maxHeight: 700,
    transition: '0.25s ease-in',
  },
}))

const listaInfluenciaArea = [
  'influenciaArea.equacaoPadrao',
  'influenciaArea.equacaoAreaTipoUm',
  'influenciaArea.equacaoAreaTipoDois',
  'influenciaArea.equacaoAreaTipoUmFixa',
  'influenciaArea.equacaoAreaTipoDoisFixa',
]

const Cadastros = ({ paisId }) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const dispatch = useDispatch()
  const dialog = useConfirmationDialog()

  const listaGrupoTema = useSelector(getGrupoTemas)
  const listaGrupos = useSelector(getGrupos)
  const listaTemas = useSelector(getTemas)
  const listaQualificadores = useSelector(getQualificadores)
  const isSaving = useSelector(getIsSavingGrupoTema)
  const tipoUsuarioLogado = useSelector(getTipoUsuarioLogado)

  const [controlAdded, setControlAdded] = useState(false)
  const [openItem, setOpenItem] = useState('')

  const desabilitarFormulario = Boolean(tipoUsuarioLogado === TIPO_USUARIO.ADMIN.OBSERVADOR)

  const validation = yup.object({
    grupoId: yup.string().required(),
    temaId: yup.string().required(),
    identificador: yup.string().required(),
    nome: yup.string().required(),
    influenciaArea: yup.string().required(),
    listaQualificadores: yup.array().min(1),
  })

  const handleExpand = (item) => {
    setOpenItem(openItem === item ? '' : item)
  }

  const handleAdded = (values, form) => {
    const data = [...listaGrupoTema, values].map((item) => ({
      ...item,
      paisId,
      qualificadorGrupoTemas: item.listaQualificadores.map((q) => ({
        id: item.qualificadorGrupoTemas?.find((i) => i.qualificadorId === q.id).id,
        qualificadorId: q.id,
        grupoTemaId: item.id,
      })),
    }))
    dispatch(callSalvarListaGrupoTema(data))
    form.reset()
    Object.keys(values).forEach((key) => {
      form.resetFieldState(key)
    })
    setControlAdded(false)
  }

  const handleSave = (values) => {
    const data = [...filter(listaGrupoTema, (u) => u.id !== values.id), values].map((item) => ({
      ...item,
      paisId,
      qualificadorGrupoTemas: item.listaQualificadores.map((q) => ({
        id: item.qualificadorGrupoTemas?.find((i) => i.qualificadorId === q.id).id,
        qualificadorId: q.id,
        grupoTemaId: item.id,
      })),
    }))
    dispatch(callSalvarListaGrupoTema(data))
  }

  const handleDelete = (id, nome, identificador) => {
    handleExpand('')
    return dialog.showConfirmation({
      title: t('dialogs.confirmarExclusao'),
      message: t('dialogs.excluirCadastro', { cadastro: `${identificador} - ${nome}` }),
    }).then((result) => {
      if (result) {
        if (id) return dispatch(callExcluirGrupoTema({ id }))
      }
      return null
    })
  }

  const getOptions = (options) => (
    options.map((option) => ({
      label: option.identificador,
      value: option.id,
    }))
  )

  const fieldsForm = () => (
    <>
      <Grid item xs={12} sm={4}>
        <FieldAutocomplete
          required
          label={t('inputs.grupo')}
          options={getOptions(listaGrupos)}
          name="grupoId"
          disabled={desabilitarFormulario}
        />

      </Grid>
      <Grid item xs={12} sm={4}>
        <FieldAutocomplete
          required
          label={t('inputs.tema')}
          options={getOptions(listaTemas)}
          name="temaId"
          disabled={desabilitarFormulario}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <FieldText
          required
          name="identificador"
          label={t('inputs.identificadorCadastro')}
          disabled={desabilitarFormulario}
        />
      </Grid>
      <Grid item xs={12} sm={12}>
        <FieldText
          required
          multiline
          minRows={5}
          maxRows={5}
          label={t('inputs.nome')}
          name="nome"
          disabled={desabilitarFormulario}
        />
      </Grid>
      <Grid item xs={12} sm={12}>
        <FieldSelect
          required
          label={t('inputs.influenciaArea')}
          name="influenciaArea"
          options={listaInfluenciaArea.map((item) => ({
            label: t(item),
            value: item,
          }))}
          disabled={desabilitarFormulario}
        />
      </Grid>
      <Grid item xs={12} sm={12}>
        <FieldAutocomplete
          multiple
          options={listaQualificadores}
          label={`${t('inputs.qualificadoresAcao')} *`}
          name="listaQualificadores"
          getOptionLabel={(option) => option.identificador || ''}
          getOptionSelected={(option, value) => option.id === value.id}
          disabled={desabilitarFormulario}
        />
      </Grid>
    </>
  )

  return (
    <>
      <div className={classes.sectionTitle}>
        <Typography className={classes.screenTitle}>
          {t('paisesCalculos.cadastros')}
        </Typography>
        <Button
          variant="default"
          disabled={controlAdded || desabilitarFormulario}
          onClick={() => {
            setControlAdded(true)
            handleExpand('')
          }}
        >
          {t('paisesCalculos.adicionarCadastro')}
        </Button>
      </div>

      <div
        className={clsx(
          classes.formAdd,
          controlAdded && classes.openFormAdd,
        )}
      >
        <Form
          onSubmit={handleAdded}
          validate={validateFormValues(validation)}
          initialValues={{
            grupoId: null,
            temaId: null,
            identificador: '',
            nome: '',
            influenciaArea: null,
            listaQualificadores: null,
          }}
          render={({ handleSubmit, form, values, pristine, valid }) => (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {fieldsForm()}
                <Grid item xs={12}>
                  <Grid
                    container
                    direction="row"
                    spacing={2}
                    justifyContent="space-between"
                  >
                    <Grid item className={classes.deleteButton}>
                      <Button
                        variant="danger"
                        type="button"
                        onClick={() => {
                          form.reset()
                          Object.keys(values).forEach((key) => {
                            form.resetFieldState(key)
                          })
                          setControlAdded(false)
                        }}
                      >
                        {t('inputs.cancelar')}
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        type="submit"
                        disabled={Boolean(pristine || !valid) || desabilitarFormulario}
                        loading={isSaving}
                      >
                        {t('inputs.salvar')}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          )}
        />
      </div>

      <div className={classes.group}>
        {listaGrupoTema.map((item, index) => (
          <Form
            key={index}
            onSubmit={handleSave}
            validate={validateFormValues(validation)}
            initialValues={{
              identificador: item?.identificador ?? '',
              nome: item?.nome ?? '',
              grupoId: item?.grupoId ?? '',
              temaId: item?.temaId ?? '',
              influenciaArea: item?.influenciaArea ?? '',
              listaQualificadores: item?.listaQualificadores ?? '',
              id: item?.id ?? '',
              grupo: item?.grupo ?? '',
              tema: item?.tema ?? '',
            }}
            render={({ handleSubmit, form, pristine, valid }) => (
              <form onSubmit={handleSubmit}>
                <Accordion
                  hideButton
                  variant="no-borders"
                  classes={{ title: classes.itemTitle }}
                  title={`${item.identificador} - ${item.nome}`}
                  onDelete={() => handleDelete(item.id, item.nome, item.identificador)}
                  expanded={index === openItem}
                  onExpand={() => handleExpand(index)}
                  disableDelete={desabilitarFormulario}
                >
                  <Grid container spacing={3}>
                    {fieldsForm()}
                    <Grid item xs={12}>
                      <Grid
                        container
                        direction="row"
                        spacing={2}
                        justifyContent="space-between"
                      >
                        <Grid item className={classes.deleteButton}>
                          <Button
                            variant="danger"
                            type="button"
                            onClick={() => {
                              form.reset(); handleExpand('')
                            }}
                          >
                            {t('inputs.cancelar')}
                          </Button>
                        </Grid>
                        <Grid item>
                          <Button
                            type="submit"
                            disabled={Boolean(pristine || !valid) || desabilitarFormulario}
                            loading={isSaving}
                          >
                            {t('inputs.salvar')}
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Accordion>
                <div className={classes.divider} />
              </form>
            )}
          />
        ))}
      </div>
    </>
  )
}

Cadastros.propTypes = {
  paisId: PropTypes.number,
}

export default Cadastros
